import {
  customDateFormatter,
  intl,
  intlFmt,
  Maskable,
  NotificationType,
  TextInput,
  toast
} from 'ehs-react';
import type { ClipboardEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { usePortletStore } from '../App';
import type { at } from '../generated/Beans';
import CommunicationUtil from '../util/CommunicationUtil';
import { InlineError } from './InlineError';

type OtpSendDetails = at.sense.portlets.saf.patientselfonboarding.model.ui.OtpSendDetails;
type OtpSendChannel = at.sense.portlets.saf.patientselfonboarding.model.ui.OtpSendChannel;

enum SendingState {
  NotSent,
  Sending,
  Sent
}

function removeWhiteSpaces(value: string) {
  return value.replace(/\s/g, '');
}

function isNumericString(text: string): boolean {
  return /^\d+$/.test(text);
}

const getInputElement = (index: number) => {
  return document.querySelector(
    `.otp-input${index} input[type="text"]`
  ) as HTMLInputElement;
};
const focusInput = (index: number) => {
  const element = getInputElement(index);
  if (element) {
    setTimeout(() => element.focus(), 10);
  }
};
const clearValue = (index: number) => {
  const element = getInputElement(index);
  if (element) {
    element.value = '';
  }
};

const initializeInputValues = (lengthOfCode: number) => {
  return Array.from({ length: lengthOfCode }, () => '#');
};

function OtpInput({
  authenticationCode,
  onChange,
  hasError
}: Readonly<{
  authenticationCode: string[];
  lengthOfCode: number;
  onChange: (code: string[]) => boolean;
  hasError: boolean;
}>): JSX.Element {
  const elements: JSX.Element[] = [];
  const setActivationCodeArray = (text: string, index: number) => {
    const newAuthenticationCode = [...authenticationCode];
    newAuthenticationCode[index] = removeWhiteSpaces(text);
    return newAuthenticationCode;
  };

  const changeActivationCode = (code: string | string[], index?: number) => {
    const newActivationCodeArray =
      typeof code === 'string'
        ? setActivationCodeArray(code, index ?? 0)
        : code;
    const validationTriggered = onChange(newActivationCodeArray);
    if (validationTriggered) {
      focusInput(0);
    }
  };

  const onPasteFunction = (e: ClipboardEvent<HTMLInputElement>) => {
    const content = e.clipboardData.getData('Text');
    const codeAsArray = [];
    for (let i = 0; i < content.length; i++) {
      const digit = content.charAt(i);
      if (isNumericString(digit)) {
        codeAsArray.push(digit);
      }
    }
    if (codeAsArray.length === authenticationCode.length) {
      changeActivationCode(codeAsArray);
    } else {
      toast(
        intl(
          'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
        ),
        NotificationType.WARN,
        undefined
      );
    }
  };

  const createOtpInput = (index: number) => {
    return (
      <TextInput
        key={'otp-input' + index}
        debounceWait={1000}
        label={''}
        value={authenticationCode[index]}
        onFocus={() => {
          if (!isNumericString(authenticationCode[index])) {
            setActivationCodeArray('', index);
          }
        }}
        onPaste={onPasteFunction}
        onKeyDown={e => {
          const element = e.target as HTMLInputElement;
          if (isNumericString(e.key)) {
            element.value = e.key;
            changeActivationCode(element.value, index);
            focusInput(index + 1);
          } else if (e.key === 'Backspace') {
            focusInput(index - 1);
            clearValue(index - 1);
            changeActivationCode('', index - 1);
          } else if (e.key === 'Delete') {
            element.value = '';
          } else if (e.key === 'ArrowRight') {
            focusInput(index + 1);
          } else if (e.key === 'ArrowLeft') {
            focusInput(index - 1);
          }
          const isCtrlV = e.key === 'v' && e.ctrlKey;
          if (e.key !== 'Tab' && !isCtrlV) {
            e.preventDefault();
          }
        }}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className={'otp-input' + index}
        autoFocus={false}
        aria-label={intlFmt(
          'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.enterDigitHint',
          index + 1
        )}
        aria-describedby="activation-code-sent-description"
      />
    );
  };

  for (let i = 0; i < authenticationCode.length; i++) {
    elements.push(createOtpInput(i));
  }
  return (
    <div className={`otp-input-container${hasError ? ' error' : ''}`}>
      {elements}
    </div>
  );
}

type ErrorObj = {
  isError: boolean;
  errorMsg: string | undefined;
};

function createNoErrorObj(): ErrorObj {
  return {
    isError: false,
    errorMsg: undefined
  };
}

function AuthenticationWizardOtpResendTimer({
  onTimeout
}: Readonly<{
  onTimeout: () => void;
}>): JSX.Element {
  const [timeLeft, setTimeLeft] = useState(90);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      setTimeLeft(prevTimeLeft => {
        if (prevTimeLeft === 0) {
          clearInterval(intervalIdRef.current!);
          onTimeoutRef.current();
          return 0;
        }
        return prevTimeLeft - 1;
      });
    }, 1000);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const printableTime = customDateFormatter({
    second: '2-digit',
    minute: 'numeric'
  }).formatDate(new Date(timeLeft * 1000));

  return (
    <p className="otp-resend-timer font-16 s-font">
      {intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeResendTimer',
        printableTime
      )}
    </p>
  );
}

function AuthenticationWizardOtpConfirmationPage({
  onNextPage
}: Readonly<{
  onNextPage: () => void;
}>): JSX.Element {
  const [lengthOfCode] = usePortletStore(state => [
    state.configuration.lengthOfOtp
  ]);
  const [sendingState, setSendingState] = useState(SendingState.NotSent);
  const [sendingError, setSendingError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [hasValidationError, setHasValidationError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [authenticationCode, setAuthenticationCode] = useState(
    initializeInputValues(lengthOfCode)
  );
  const [isConfirmRequestPending, setIsConfirmRequestPending] = useState(false);
  const [isResendDisabledAfterError, setIsResendDisabledAfterError] = useState(
    false
  );
  const [showResendTimer, setShowResendTimer] = useState(false);
  const [sendingDetails, setSendingDetails] = useState({
    sendMethod: 'SMS' as OtpSendChannel,
    maskedRecipient: ''
  });

  const triggerResend = () => {
    setSendingState(SendingState.NotSent);
    setHasValidationError(createNoErrorObj());
    setSendingError(createNoErrorObj());
    setIsResendDisabledAfterError(false);
    setShowResendTimer(true);
  };

  const getActivationCodeSentToastMessage = (
    sendingDetails: OtpSendDetails
  ): string => {
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaEmailToast',
        sendingDetails.maskedRecipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaSmsToast',
        sendingDetails.maskedRecipient
      );
    }
    return '';
  };

  const getActivationCodeSentDescription = (
    sendingDetails: OtpSendDetails,
    lengthOfCode: number
  ): string => {
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeEmailDescription',
        lengthOfCode,
        sendingDetails.maskedRecipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSmsDescription',
        lengthOfCode,
        sendingDetails.maskedRecipient
      );
    }
    return '';
  };

  const validateAuthenticationCode = (authenticationCode: string) => {
    CommunicationUtil.validateAuthenticationCode(
      removeWhiteSpaces(authenticationCode),
      () => onNextPage(),
      (errorMsg?) => {
        setAuthenticationCode(initializeInputValues(lengthOfCode));
        setHasValidationError({
          isError: true,
          errorMsg: errorMsg
        });
      },
      (pending: boolean) => setIsConfirmRequestPending(pending)
    );
  };

  useEffect(() => {
    if (
      sendingState === SendingState.Sent &&
      sendingDetails.maskedRecipient.length > 0
    ) {
      toast(
        getActivationCodeSentToastMessage(sendingDetails),
        NotificationType.SUCCESS,
        undefined
      );
    }
  }, [sendingState, sendingDetails]);

  // Render function
  if (sendingState === SendingState.Sent) {
    const showResendLink =
      !isResendDisabledAfterError &&
      !isConfirmRequestPending &&
      !showResendTimer;
    return (
      <div>
        <p
          className="activation-code-description font-16 s-font"
          id="activation-code-sent-description"
        >
          {getActivationCodeSentDescription(sendingDetails, lengthOfCode)}
        </p>
        {hasValidationError.isError && (
          <InlineError
            title={intl(
              'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
            )}
            text={
              hasValidationError.errorMsg ??
              intl(
                'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedText'
              )
            }
          />
        )}
        <OtpInput
          authenticationCode={authenticationCode}
          lengthOfCode={lengthOfCode}
          onChange={authCode => {
            setHasValidationError(createNoErrorObj());
            setAuthenticationCode(authCode);

            const currentCode = authCode.join('');

            if (
              currentCode.length >= lengthOfCode &&
              isNumericString(currentCode)
            ) {
              validateAuthenticationCode(currentCode);
              return true;
            }
            return false;
          }}
          hasError={hasValidationError.isError}
        />
        {showResendLink && (
          <p className="otp-resend-link-container font-16 s-font">
            {intl(
              'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeTakingTooLong'
            )}
            &nbsp;
            <button
              className="otp-resend-link"
              onClick={() => triggerResend()}
              onKeyUp={e => {
                if (e.key === ' ') {
                  triggerResend();
                }
              }}
            >
              {intl(
                'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSendAgain'
              )}
            </button>
          </p>
        )}
        {showResendTimer && (
          <AuthenticationWizardOtpResendTimer
            onTimeout={() => {
              setShowResendTimer(false);
            }}
          />
        )}
        <Maskable
          masked={isConfirmRequestPending}
          className="authentication-content-loading-spinner"
        >
          <div className="authentication-content-loading-spinner-content" />
        </Maskable>
      </div>
    );
  } else if (sendingError.isError) {
    return (
      <div>
        <InlineError
          title={intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedTitle'
          )}
          text={
            sendingError.errorMsg ??
            intl(
              'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedText'
            )
          }
        />
      </div>
    );
  } else {
    if (sendingState === SendingState.NotSent) {
      setSendingState(SendingState.Sending);
      setShowResendTimer(true);
      CommunicationUtil.sendAuthenticationCode(
        (sendDetails: OtpSendDetails) => {
          setSendingDetails(sendDetails);
          setSendingState(SendingState.Sent);
        },
        (msg?: string, resendDisabled?: boolean) => {
          if (resendDisabled) {
            setIsResendDisabledAfterError(true);
          }
          setSendingError({
            isError: true,
            errorMsg: msg
          });
        }
      );
    }

    return (
      <div>
        <p className="font-16 s-font">
          {intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCode'
          )}
        </p>
        <Maskable masked className="authentication-content-loading-spinner">
          <div className="authentication-content-loading-spinner-content" />
        </Maskable>
      </div>
    );
  }
}

export default AuthenticationWizardOtpConfirmationPage;
